import { useCallback, useMemo, useReducer } from "react";
import { ContractRevenueItem } from "@/pages/contracts/types";
import { CreateRevenueItemRequest } from "@/pages/contracts/hooks";
import {
  requestDates,
  requestScheduledPayments,
  requestToRevenueItem
} from "@/pages/contracts/revenueItemRequest";
import { DRAFT_CONTRACT_ID, DraftRevenueItem } from "./types";

type DraftAction =
  | { type: "deleteItems"; ids: string[] }
  | { type: "restoreItem"; id: string }
  | { type: "revertItem"; item: DraftRevenueItem }
  | { type: "addItem"; item: DraftRevenueItem }
  | { type: "updateItem"; id: string; item: Partial<ContractRevenueItem> }
  | { type: "replaceItems"; items: DraftRevenueItem[] };

const draftReducer = (
  state: DraftRevenueItem[],
  action: DraftAction
): DraftRevenueItem[] => {
  switch (action.type) {
    case "deleteItems":
      // Source rows stay visible and restorable; rows added here go outright.
      return state
        .filter(
          (item) =>
            !(
              action.ids.includes(item.id) &&
              item.contractId === DRAFT_CONTRACT_ID
            )
        )
        .map((item) =>
          action.ids.includes(item.id) ? { ...item, draftDeleted: true } : item
        );
    case "restoreItem":
      return state.map((item) =>
        item.id === action.id ? { ...item, draftDeleted: false } : item
      );
    case "revertItem":
      return state.map((item) =>
        item.id === action.item.id ? { ...action.item } : item
      );
    case "addItem":
      return [...state, action.item];
    case "updateItem":
      return state.map((item) =>
        item.id === action.id ? { ...item, ...action.item } : item
      );
    case "replaceItems":
      return action.items;
    default:
      return state;
  }
};

// The pending set and its baseline, shared by duplication and the Update terms editor.
export const useTermsDraft = (sourceItems: ContractRevenueItem[]) => {
  const [items, dispatch] = useReducer(draftReducer, sourceItems, (source) =>
    source.map((item) => ({ ...item }))
  );

  const baseline = useMemo(
    () => Object.fromEntries(sourceItems.map((item) => [item.id, item])),
    [sourceItems]
  );

  const addItem = useCallback((request: CreateRevenueItemRequest) => {
    dispatch({
      type: "addItem",
      item: requestToRevenueItem(
        request,
        crypto.randomUUID(),
        DRAFT_CONTRACT_ID
      ) as DraftRevenueItem
    });
  }, []);

  const updateItem = useCallback(
    (id: string, request: CreateRevenueItemRequest) => {
      dispatch({
        type: "updateItem",
        id,
        item: {
          ...(request as unknown as Partial<ContractRevenueItem>),
          ...(requestDates(request) as Partial<ContractRevenueItem>),
          // The edit recomputes totalLocal only; the server re-derives total on save.
          total: null,
          scheduledPayments: requestScheduledPayments(request, id)
        }
      });
    },
    []
  );

  const deleteItems = useCallback(
    (ids: string[]) => dispatch({ type: "deleteItems", ids }),
    []
  );

  const restoreItem = useCallback(
    (id: string) => dispatch({ type: "restoreItem", id }),
    []
  );

  // Puts a modified row back to the values it had when the session opened.
  const revertItem = useCallback(
    (id: string) => {
      const original = baseline[id];
      if (original) {
        dispatch({ type: "revertItem", item: { ...original } });
      }
    },
    [baseline]
  );

  const replaceItems = useCallback(
    (next: DraftRevenueItem[]) =>
      dispatch({ type: "replaceItems", items: next }),
    []
  );

  return {
    items,
    baseline,
    addItem,
    updateItem,
    deleteItems,
    restoreItem,
    revertItem,
    replaceItems
  };
};

export type TermsDraft = ReturnType<typeof useTermsDraft>;
